(ns manage.ad-variant.approval.modal
  (:require ["@mui/icons-material/Report$default" :as ReportIcon]
            ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/AlertTitle$default" :as AlertTitle]
            ["@mui/material/Autocomplete$default" :as Autocomplete]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/ListItem$default" :as ListItem]
            ["@mui/material/ListItemText$default" :as ListItemText]
            ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/Select$default" :as Select]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Tab$default" :as Tab]
            ["@mui/material/Tabs$default" :as Tabs]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/material/Typography$default" :as Typography]
            ["src/svg/artwork/approvals.svg$default" :as ApprovalsArtwork]
            ["src/svg/inform/error-triangle-fill.svg$default" :as AlertIcon]
            ["src/svg/person/person-circle-filled.svg$default" :as PersonIcon]
            [clojure.set :refer [rename-keys]]
            [common.modal.view :refer [defmodal modal-v2]]
            [common.patterns.empty-state-view :refer [empty-state-view]]
            [common.queries :refer [use-infinite-query use-mutation use-query]]
            [common.resource.query.adset :refer [adset-query]]
            [common.resource.query.advertiser :refer [advertiser-query]]
            [common.resource.query.agency :refer [agency-query]]
            [common.resource.query.user :refer [user-query]]
            [common.uix.hooks :as uix.hooks]
            [common.util :as util]
            [common.widgets.form-field :refer [form-field]]
            [manage.ad-variant.approval.db :as db]
            [manage.ad-variant.queries :as q]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def status-map
  {"active" "Active Reviewer"
   "revoked" "Revoked"})

(defui reviewer-list-item [{:keys [reviewer reviewers set-reviewers]}]
  ($ Stack
     {:direction "row"
      :justifyContent "space-between"}

     ($ MenuItem
        {:sx #js {"&.Mui-disabled" {:opacity 1}}}
        ($ ListItem
           {:sx #js {:width "40px"
                     :paddingLeft "0px"}}
           ($ PersonIcon))
        ($ ListItemText
           {:secondary (:value reviewer)}
           (:label reviewer)))
     ($ Select {:value (:status reviewer)
                :size "small"
                :variant "standard"
                :renderValue (fn [value]
                               ($ Stack {:direction "row"
                                         :gap 1}
                                  ($ Typography (get status-map value))))
                :onChange #(set-reviewers (-> (filter (fn [r] (not= (:value r) (:value reviewer))) reviewers)
                                              (conj (assoc reviewer :status (-> % .-target .-value keyword)))))
                :sx #js {:width "160px"}}
        ($ MenuItem {:value :active} "Active reviewer")
        ($ MenuItem {:value :revoked} "Unassign as reviewer"))))

(defui reviewer-view [{:keys [reviewers set-reviewers]}]
  ($ Typography {:component "span"}
     ($ Stack {:sx #js {:width 580
                        :minHeight 360
                        :py "3rem"
                        :gap 3
                        :flex 1
                        :overflow "hidden"}}
        (if (> (count reviewers) 0)
          (for [reviewer (sort-by :value reviewers)]
            ($ reviewer-list-item {:key (:label reviewer) :reviewer reviewer :reviewers reviewers :set-reviewers set-reviewers}))
          ($ empty-state-view
             {:title "No reviewers requested"
              :description "Add reviewer(s) by creating a new approval request and entering the email address(es) of those you want to conduct the review."
              :artwork ApprovalsArtwork})))))

(defui selected-failed-renders-message [{:keys [render-failed-selection-count]}]
  ($ Stack
     {:direction "row"
      :justifyContent "space-between"
      :alignItems "center"}
     ($ Typography {:component "span"
                    :sx #js {:color "grey.30"}}
        (str render-failed-selection-count " ad variant(s) have failed renders and can't be marked for internal review."))
     ($ Button
        {:onClick #(rf/dispatch [:modal/close])}
        "Edit")))

(defui approval-request-view [{:keys [users reviewers set-reviewers selection-count render-failed-selection-count message set-message]}]
  (let [users-and-clients false] ; Will be picked up in future ticket when external users are added to the flow
    ($ Stack
       {:sx #js {:width 580
                 :minHeight 360
                 :py "3rem"
                 :gap 3
                 :flex 1
                 :overflow "hidden"}}
       ($ form-field
          {:label "Reviewer(s)"
           :required? true}
          ($ Autocomplete
             {:multiple true
              :size "small"
              :limitTags 5
              :sx #js {:mt "8px"}
              :value (to-array reviewers)
              :options users
              :getOptionLabel #(str (:label %) " - " (:value %))
              :getOptionKey #(str (:label %) " - " (:value %))
              :fullWidth true
              :onChange (fn [_ value _ _ _]
                          (set-reviewers value))
              :renderInput (fn [^js params]
                             (when (= 0 (count reviewers)) (set! (.-placeholder params) "Enter the email address of who you want to conduct the review"))
                             ($ TextField params))}))
       (if (> selection-count 0)
         ($ form-field
            {:label "Ad variants"}
            ($ Stack
               {:direction "row"
                :justifyContent "space-between"
                :alignItems "center"}
               ($ Typography
                  {:component "span"
                   :sx #js {:color "grey.30"}}
                  (str selection-count " ad variant(s) will be marked for internal review"))
               ($ Button
                  {:onClick #(rf/dispatch [:modal/close])}
                  "Edit"))
            (when (> render-failed-selection-count 0)
              ($ selected-failed-renders-message {:render-failed-selection-count render-failed-selection-count})))
         ($ Stack
            {:sx #js {:py "3rem"
                      :gap 3
                      :flex 1
                      :overflow "hidden"}}
            ($ Alert
               {:severity "warning"
                :icon ($ AlertIcon)}
               ($ AlertTitle "Approval request contains no ad variants")
               ($ Stack
                  {:direction "column"
                   :sx #js {:alignItems "flex-start"
                            :gap 1.2
                            :py 1}}
                  "Select the ad variants you want to be reviewed to continue. Ad variants selected will have their status changed to Internal review."
                  ($ Button
                     {:variant "outlined"
                      :onClick #(rf/dispatch [:modal/close])}
                     "Select ad variants")))
            (when (> render-failed-selection-count 0)
              ($ form-field
                 {:label "Ad variants"}
                 ($ selected-failed-renders-message {:render-failed-selection-count render-failed-selection-count})))))

       (when users-and-clients
         ($ Alert
            {:severity "error"
             :icon ($ ReportIcon {:color "red"})}
            ($ AlertTitle "Approval request contains both internal users and clients")
            ($ Stack
               {:sx #js {:direction "column"
                         :alignItems "flex-start"
                         :gap 1.2
                         :py 1}}
               "An approval request can only be sent to a single group of reviewers at once; either internal users or clients. To continue, update the list of reviewers to only contain either internal users or clients.")))

       ($ form-field
          {:label "Message"}
          ($ TextField
             {:placeholder "Include any additional notes or instructions..."
              :fullWidth true
              :multiline true
              :value message
              :minRows 5
              :onChange (fn [e] (set-message (.. e -target -value)))})))))

(defui modal-content [{:keys [active-tab set-active-tab users reviewers set-reviewers message set-message]}]
  (let [selection* (use-subscribe [:manage.ad-variant.page.db/selection])
        {:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {:keys [data]} (use-infinite-query (q/ad-variants-query {:advertiser-id advertiser-id
                                                                 :adset-id adset-id}))
        pages (:pages data)
        ad-variants (uix/use-memo (fn [] (mapcat :data pages)) [pages])
        render-failed-ad-variant-ids (->> ad-variants
                                          (filter #(= "render-failed" (-> % :data :ad-variant-status)))
                                          (map (comp :uuid :data))
                                          (set))
        selection (clojure.set/difference (set selection*) render-failed-ad-variant-ids)

        tabs (->> [{:key :request
                    :label "New approval request"
                    :icon "add"
                    :content ($ approval-request-view {:users users
                                                       :selection-count (count selection)
                                                       :render-failed-selection-count (- (count selection*) (count selection))
                                                       :reviewers reviewers
                                                       :set-reviewers set-reviewers
                                                       :message message
                                                       :set-message set-message})}
                   ;; Managing access needs more work, hide the tab for now
                   #_{:key :reviewer
                      :label "Manage access"
                      :icon "groups"
                      :content ($ reviewer-view {:users users
                                                 :reviewers reviewers
                                                 :set-reviewers set-reviewers})}]
                  (util/index-by :key))]
    ($ Stack {:flex 1
              :overflow "hidden"}
       ($ Tabs {:value active-tab
                :onChange (fn [_ tab]
                            (set-active-tab (keyword tab)))
                :sx #js {".MuiTabs-flexContainer" #js {:gap 2}}}
          (for [[key {:keys [label icon]}] tabs]
            ($ Tab {:key key
                    :value key
                    :label label
                    :icon ($ Icon icon)
                    :iconPosition "start"
                    :sx #js {:px 0
                             :py 2
                             :minWidth "unset"
                             :minHeight "unset"}})))
       (get-in tabs [active-tab :content]))))

(defui modal-footer [{:keys [active-tab set-active-tab reviewers reviewers-already-assigned message]}]
  (let [{:keys [agency-id advertiser-id adset-id]} (use-subscribe [:url-context])
        {set-approval :mutate} (use-mutation (q/set-approval-mutation {:advertiser-id advertiser-id
                                                                       :adset-id adset-id}))

        {{agency-name :name} :data} (use-query (agency-query {:agency-id agency-id}))
        {{advertiser-name :name} :data} (use-query (advertiser-query {:advertiser-id advertiser-id}))
        {{adset-name :name} :data} (use-query (adset-query {:adset-id adset-id}))
        {{user-name :name} :data} (use-query (user-query))

        active-reviewers (->> reviewers
                              (filter #(and (= :active (:status %))
                                            (not (get reviewers-already-assigned (:id %)))))
                              (map #(-> %
                                        (dissoc :status)
                                        (rename-keys {:label :name :value :email})))
                              vec)
        revoked-reviewers (->> reviewers
                               (filter #(= :revoked (:status %))))
        user-ids (vec (map :id active-reviewers))
        selection (use-subscribe [:manage.ad-variant.page.db/selection])
        review (use-subscribe [::db/review])
        url-context (use-subscribe [:url-context])
        preview-url (str js/window.location.origin (rfe/href :adset/ad-variants url-context {:ad-variants-preview true
                                                                                             :review true}))]
    ($ Stack {:direction "row" :gap 1}
       ($ Button {:variant "text"
                  :onClick #(do (rf/dispatch [:modal/close])
                                (set-active-tab :request))
                  :sx #js {:color "text.primary"}}
          "Cancel")
       ($ Button {:variant "contained"
                  :onClick #(do
                              (when (pos? (count selection))
                                (set-approval {:approval "internal-review"
                                               :uuids selection}))
                              (when (pos? (count active-reviewers))
                                (rf/dispatch [:manage.ad-variant.approval.db/assign-to-review
                                              {:adset-id adset-id
                                               :user-ids user-ids
                                               :users active-reviewers
                                               :message message
                                               :preview-url preview-url
                                               :agency-name agency-name
                                               :advertiser-name advertiser-name
                                               :adset-name adset-name
                                               :current-user-name user-name}]))
                              (when (pos? (count revoked-reviewers))
                                (doall (for [revoked-reviewer revoked-reviewers]
                                         (rf/dispatch [:manage.ad-variant.approval.db/revoke-review
                                                       {:adset-id adset-id
                                                        :user-id (:id revoked-reviewer)
                                                        :request-id (:request/id review)}]))))
                              (rf/dispatch [:modal/close]))}
          (if (= active-tab :request) "Send" "Save")))))

(defmodal approval :ad-variant/approval
  [{:keys [users]}]
  (let [[active-tab set-active-tab] (uix.hooks/use-state {:initial-value :request
                                                          :path [::db/state :mode]})
        [message set-message] (uix/use-state "")
        loaded-reviewers (use-subscribe [::db/reviewers])
        loaded-reviewers* (for [user loaded-reviewers]
                            (-> (filter #(= (:user-request-mapping/user-id user) (:id %)) users)
                                first
                                (assoc :status (:user-request-mapping/user-status user))))

        [reviewers set-reviewers] (uix/use-state (vec loaded-reviewers*))]
    ($ modal-v2
       {:title "Approval request"
        :footer ($ modal-footer {:active-tab active-tab
                                 :set-active-tab set-active-tab
                                 :reviewers reviewers
                                 :reviewers-already-assigned (set (map :id (filter #(= "active" (:status %))
                                                                                   loaded-reviewers*)))
                                 :message message})}
       ($ modal-content {:active-tab active-tab
                         :set-active-tab set-active-tab
                         :users users
                         :reviewers reviewers
                         :set-reviewers set-reviewers
                         :message message
                         :set-message set-message}))))

(defmodal no-user-access :ad-variant/no-user-access []
  ($ modal-v2 {:title "Missing permission"
               :footer ($ Button {:variant "contained" :on-click #(rf/dispatch [:modal/close])} "OK")}
     ($ Stack
        {:direction "column"}
        ($ Typography {:component "span"
                       :sx #js {:color "grey.30"}}
           "Request the 'user & role management' permission to be added to your role.")
        ($ Typography {:component "span"
                       :sx #js {:color "grey.30"}}
           "Please contact your admin or press help and support on the top left and send a message to support."))))
